import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Copy,
  Check,
  Key,
  Code as CodeIcon,
  Gamepad2,
  AlertTriangle,
  RotateCw,
  ChevronRight,
  Eye,
  EyeOff,
} from "lucide-react";

import { Card, CardBody, CardHeader } from "../../components/Card/Card";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { Badge } from "../../components/Badge/Badge";
import { Avatar } from "../../components/Avatar/Avatar";
import { Code } from "../../components/Code/Code";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { useToast } from "../../components/Toast/Toast";
import { ApiError, describeError } from "../../lib/errors";
import { useEndpoints } from "../auth/useAuth";
import { getProject } from "../projects/api";
import { getApiKey, rotateApiKey } from "./api";
import { RotateConfirmModal } from "./RotateConfirmModal";
import { RevealKeyModal } from "./RevealKeyModal";
import styles from "./ApiKeysPage.module.css";

/** Repository links used by the two "learn more" rows. */
const SERVER_API_DOCS =
  "https://gitlab.furylion.dev/furylion/social-hub/-/blob/main/docs/api.md";
const LEADERBOARDS_DOCS = `${SERVER_API_DOCS}#leaderboards`;
const UNITY_SDK_DOCS =
  "https://gitlab.furylion.dev/furylion/unity-modules/socialhub-sdk";

/** Placeholder for a value the bootstrap payload has not delivered (yet). */
const MISSING = "—";

/**
 * Format an ISO date into a short, human-friendly string. Returns "—" when
 * the input is falsy so empty cells render the canonical placeholder.
 */
function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
}

export function ApiKeysPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { push } = useToast();
  const qc = useQueryClient();
  const { data: project } = useQuery({
    queryKey: ["project", id, "overview"],
    queryFn: () => getProject(id).then((r) => r.data),
    enabled: !!id,
  });
  // Current API key read-state. Degrades gracefully — a 404 or error leaves
  // `apiKey` undefined and the card falls back to em-dash placeholders.
  const apiKeyQ = useQuery({
    queryKey: ["project", id, "api-keys"],
    queryFn: () => getApiKey(id),
    enabled: !!id,
  });
  const apiKey = apiKeyQ.data;
  // Game-facing addresses of this deployment, from the /auth/me bootstrap
  // payload the shell already fetched.
  const endpoints = useEndpoints();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [keyVisible, setKeyVisible] = useState(false);
  // Which copy target flashed "Copied" last — one slot at a time.
  const [copiedSlot, setCopiedSlot] = useState<string | null>(null);

  const last4Display = apiKey?.last4 ?? "••••";
  const plaintext = apiKey?.api_key_plaintext ?? "";

  async function copyValue(slot: string, value: string, toast?: string) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedSlot(slot);
      setTimeout(
        () => setCopiedSlot((current) => (current === slot ? null : current)),
        2000,
      );
      if (toast) push({ variant: "success", message: toast });
    } catch {
      // Clipboard unavailable (e.g. non-secure context) — silently ignore.
    }
  }

  // The route param *is* games.id — the Game ID a client sends to
  // POST /v1/auth/anonymous — so it needs no round trip to render.
  const gameId = project?.id ?? id;
  const restBaseUrl = endpoints?.rest_base_url ?? "";
  const webSocketUrl = endpoints?.websocket_url ?? "";

  const credentials = [
    { slot: "game-id", label: "Game ID", value: gameId },
    { slot: "rest-url", label: "REST URL", value: restBaseUrl },
    { slot: "ws-url", label: "WebSocket", value: webSocketUrl },
  ];

  // Submit URL for the curl example. rest_base_url carries a trailing slash;
  // trim it so the joined path never doubles up.
  const submitUrl = `${(restBaseUrl || MISSING).replace(/\/+$/, "")}/v1/internal/leaderboards/$LB_ID/submit`;
  const serverSnippet = `# Write a score for a player — no player JWT required.
curl -X POST "${submitUrl}" \\
  -H "X-Api-Key: $SOCIALHUB_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d "{\\"player_id\\": \\"$PLAYER_ID\\", \\"score\\": 1850}"

# 200 → {"applied": true, "score": 1850, "rank": 12, "delta": 42}
# 401 → key missing or unknown   404 → no such leaderboard in this project`;

  const unitySnippet = `var client = new SocialHubClient(new SocialHubClientOptions
{
    RestBaseUrl  = new Uri("${restBaseUrl || MISSING}"),
    WebSocketUrl = new Uri("${webSocketUrl || MISSING}"),
    GameId       = Guid.Parse("${gameId || MISSING}"),
});

// Player session — anonymous login returns the JWT every call uses.
await client.Auth.LoginAnonymousAsync();`;

  const breadcrumb = [
    { label: "All projects", to: "/projects" },
    { label: project?.name ?? "Project", to: `/projects/${id}/overview` },
    { label: "API Keys" },
  ];

  // Fingerprint hash display — render `sha256:{12}…` when the digest is
  // present, else `sha256:…{last4}` when only last4 is available, else dash.
  let fingerprintHashDisplay: string | null = null;
  if (apiKey?.fingerprint) {
    fingerprintHashDisplay = `sha256:${apiKey.fingerprint.slice(0, 12)}…`;
  } else if (apiKey?.last4) {
    fingerprintHashDisplay = `sha256:…${apiKey.last4}`;
  }

  // "Last rotated" — if no rotated_at, fall back to "Never" (or "—" while the
  // api-keys query is still loading / errored out).
  const lastRotatedDisplay = apiKey?.rotated_at
    ? formatDate(apiKey.rotated_at)
    : apiKey
      ? "Never"
      : "—";

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} title="API Keys" />
      <div className={styles.page}>
        {/* Server API key card ------------------------------------------- */}
        <Card>
          <CardHeader>
            <div className={styles.headerStack}>
              <div className={styles.headerTitleRow}>
                <span className={styles.iconAccent} aria-hidden="true">
                  <Key size={16} />
                </span>
                <h3 className={styles.headerTitle}>Server API key</h3>
                <Badge variant="success" dot>
                  Active
                </Badge>
              </div>
              <span className={styles.headerSub}>
                One active key per project · server-to-server only
              </span>
            </div>
          </CardHeader>
          <CardBody>
            <p className={styles.bodyLead}>
              Used for server-to-server calls from your game backend. The Unity
              SDK does not need it — see <b>Unity SDK credentials</b> below.
            </p>

            <div className={styles.keyDisplay}>
              {keyVisible && plaintext ? (
                <code className={styles.keyFull}>{plaintext}</code>
              ) : (
                <>
                  <span className={styles.keyMask}>
                    ••••••••••••••••••••••••••••••••
                  </span>
                  <span className={styles.keyTail}>{last4Display}</span>
                </>
              )}
              <div className={styles.keyActions}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setKeyVisible((v) => !v)}
                  disabled={!plaintext}
                  aria-label={keyVisible ? "Hide API key" : "Show API key"}
                >
                  {keyVisible ? (
                    <EyeOff size={12} aria-hidden="true" />
                  ) : (
                    <Eye size={12} aria-hidden="true" />
                  )}
                  <span>{keyVisible ? "Hide" : "Show"}</span>
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void copyValue(
                      "key",
                      plaintext,
                      "API key copied to clipboard.",
                    )
                  }
                  disabled={!plaintext}
                  aria-label="Copy API key"
                >
                  {copiedSlot === "key" ? (
                    <Check size={12} aria-hidden="true" />
                  ) : (
                    <Copy size={12} aria-hidden="true" />
                  )}
                  <span>{copiedSlot === "key" ? "Copied" : "Copy"}</span>
                </Button>
              </div>
            </div>

            <div className={styles.keyMeta}>
              <div className={styles.metaCell}>
                <div className={styles.metaKey}>Created</div>
                <div className={styles.metaValue}>
                  {apiKey?.created_at ? (
                    formatDate(apiKey.created_at)
                  ) : (
                    <span className={styles.metaEmpty}>—</span>
                  )}
                </div>
              </div>
              <div className={styles.metaCell}>
                <div className={styles.metaKey}>Last rotated</div>
                <div className={styles.metaValue}>
                  {lastRotatedDisplay === "—" ? (
                    <span className={styles.metaEmpty}>—</span>
                  ) : (
                    lastRotatedDisplay
                  )}
                </div>
              </div>
              <div className={styles.metaCell}>
                <div className={styles.metaKey}>Rotated by</div>
                <div className={styles.metaValue}>
                  {apiKey?.rotated_by ? (
                    <>
                      <Avatar
                        size={24}
                        name={apiKey.rotated_by.email ?? "?"}
                        variant="gradient"
                      />
                      <span>{apiKey.rotated_by.email ?? "Unknown admin"}</span>
                    </>
                  ) : (
                    <span className={styles.metaEmpty}>—</span>
                  )}
                </div>
              </div>
              <div className={styles.metaCell}>
                <div className={styles.metaKey}>Fingerprint</div>
                <div className={styles.metaValue}>
                  {fingerprintHashDisplay ? (
                    <code>{fingerprintHashDisplay}</code>
                  ) : (
                    <span className={styles.metaEmpty}>—</span>
                  )}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Use from your game server card -------------------------------- */}
        <Card>
          <CardHeader>
            <div className={styles.headerStack}>
              <div className={styles.headerTitleRow}>
                <span className={styles.iconInfo} aria-hidden="true">
                  <CodeIcon size={16} />
                </span>
                <h3 className={styles.headerTitle}>
                  Use from your game server
                </h3>
              </div>
              <span className={styles.headerSub}>
                Server-to-server score submit
              </span>
            </div>
          </CardHeader>
          <CardBody>
            <p className={styles.sdkLead}>
              Send the key in the <code>X-Api-Key</code> header. Keep it in
              your server&apos;s secret store.
            </p>
            <div className={styles.snippetWrap}>
              <button
                type="button"
                className={styles.snippetCopy}
                onClick={() => void copyValue("server-snippet", serverSnippet)}
                aria-label="Copy server snippet"
              >
                {copiedSlot === "server-snippet" ? (
                  <Check size={11} aria-hidden="true" />
                ) : (
                  <Copy size={11} aria-hidden="true" />
                )}
                <span>
                  {copiedSlot === "server-snippet" ? "Copied" : "Copy"}
                </span>
              </button>
              <Code variant="block" language="bash">
                {serverSnippet}
              </Code>
            </div>
            <div className={styles.docsRow}>
              <a
                className={styles.docsLink}
                href={SERVER_API_DOCS}
                target="_blank"
                rel="noopener noreferrer"
              >
                Server API reference
                <ChevronRight size={12} aria-hidden="true" />
              </a>
              <a
                className={styles.docsLink}
                href={LEADERBOARDS_DOCS}
                target="_blank"
                rel="noopener noreferrer"
              >
                Leaderboards guide
                <ChevronRight size={12} aria-hidden="true" />
              </a>
            </div>
          </CardBody>
        </Card>

        {/* Unity SDK credentials card ------------------------------------ */}
        <Card>
          <CardHeader>
            <div className={styles.headerStack}>
              <div className={styles.headerTitleRow}>
                <span className={styles.iconUnity} aria-hidden="true">
                  <Gamepad2 size={16} />
                </span>
                <h3 className={styles.headerTitle}>Unity SDK credentials</h3>
              </div>
              <span className={styles.headerSub}>
                What actually goes into the game client
              </span>
            </div>
          </CardHeader>
          <CardBody>
            <p className={styles.sdkLead}>
              The SDK needs the Game ID and your environment&apos;s URLs — fill
              them into the <code>SocialHubConfig</code> asset (
              <em>Create ▸ Furylion ▸ Social Hub Config</em>) or pass them in
              code.
            </p>

            <div className={styles.credList}>
              {credentials.map((cred) => (
                <div className={styles.credRow} key={cred.slot}>
                  <span className={styles.credLabel}>{cred.label}</span>
                  <span className={styles.credValue}>
                    {cred.value || (
                      <span className={styles.metaEmpty}>{MISSING}</span>
                    )}
                  </span>
                  <div className={styles.keyActions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void copyValue(cred.slot, cred.value)}
                      disabled={!cred.value}
                      aria-label={`Copy ${cred.label}`}
                    >
                      {copiedSlot === cred.slot ? (
                        <Check size={12} aria-hidden="true" />
                      ) : (
                        <Copy size={12} aria-hidden="true" />
                      )}
                      <span>
                        {copiedSlot === cred.slot ? "Copied" : "Copy"}
                      </span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.snippetWrap}>
              <button
                type="button"
                className={styles.snippetCopy}
                onClick={() => void copyValue("unity-snippet", unitySnippet)}
                aria-label="Copy Unity snippet"
              >
                {copiedSlot === "unity-snippet" ? (
                  <Check size={11} aria-hidden="true" />
                ) : (
                  <Copy size={11} aria-hidden="true" />
                )}
                <span>
                  {copiedSlot === "unity-snippet" ? "Copied" : "Copy"}
                </span>
              </button>
              <Code variant="block" language="csharp">
                {unitySnippet}
              </Code>
            </div>
            <div className={styles.docsRow}>
              <a
                className={styles.docsLink}
                href={UNITY_SDK_DOCS}
                target="_blank"
                rel="noopener noreferrer"
              >
                Unity SDK — getting started
                <ChevronRight size={12} aria-hidden="true" />
              </a>
            </div>
          </CardBody>
        </Card>

        {/* Danger zone --------------------------------------------------- */}
        <section className={styles.dangerZone} aria-labelledby="dz-title">
          <div className={styles.dangerHeader}>
            <h3 id="dz-title">
              <span className={styles.iconDanger} aria-hidden="true">
                <AlertTriangle size={16} />
              </span>
              Rotate API key
            </h3>
          </div>
          <div className={styles.dangerBody}>
            <p className={styles.dangerLead}>
              Invalidates the current key immediately and issues a new one.
              Your game server loses access until it is updated. Players are
              unaffected.
            </p>
            <Alert variant="warn" title="No grace period">
              Any server still sending the old key starts receiving 401
              Unauthorized the moment you rotate.
            </Alert>
            <div>
              <Button
                variant="danger-outline"
                onClick={() => setConfirmOpen(true)}
                icon={<RotateCw size={14} aria-hidden="true" />}
              >
                Rotate API key…
              </Button>
            </div>
          </div>
        </section>

        <RotateConfirmModal
          open={confirmOpen}
          projectName={project?.name ?? ""}
          currentKeyLast4={apiKey?.last4 ?? undefined}
          onClose={() => setConfirmOpen(false)}
          onConfirmed={async () => {
            try {
              const r = await rotateApiKey(id);
              setConfirmOpen(false);
              setRevealed(r.api_key_plaintext);
              // The rotate mutated last4 / fingerprint / rotated_at — refresh
              // the api-keys card so it reflects the new key.
              await qc.invalidateQueries({
                queryKey: ["project", id, "api-keys"],
              });
              await qc.invalidateQueries({
                queryKey: ["project", id, "activity"],
              });
            } catch (err) {
              push({
                variant: "danger",
                message:
                  err instanceof ApiError
                    ? describeError(err.code)
                    : describeError("internal"),
              });
            }
          }}
        />

        <RevealKeyModal
          apiKey={revealed}
          onClose={() => setRevealed(null)}
        />
      </div>
    </>
  );
}
